import { apiFetch } from '@/lib/apiFetch';
import { handleUnauthorized } from './UserManagmentServices';

const BASE_PATH = '/api/ui/v1/teacher/profile/media/intro-video';

export interface InitUploadRequest {
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
}

export interface InitUploadResponse {
  videoUploadId: string;
  presignedUrl: string;
  expiresInSeconds: number;
}

export interface ConfirmUploadResponse {
  videoUploadId: string;
  status: string;
}

export interface ExternalLinkRequest {
  url: string;
  platform: 'YOUTUBE' | 'VIMEO' | 'INSTAGRAM';
}

export interface VideoStatusResponse {
  videoId: string | null;
  status: 'PENDING' | 'UPLOADED' | 'PROCESSING' | 'READY' | 'FAILED' | 'NONE';
  sourceType: 'UPLOADED' | 'RECORDED' | 'EXTERNAL_LINK' | null;
  platform: 'YOUTUBE' | 'VIMEO' | 'INSTAGRAM' | null;
  externalUrl: string | null;
  oembedHtml: string | null;
  thumbnailUrl: string | null;
  playbackUrl: string | null;
  failureReason: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

async function apiCall<T>(path: string, accessToken: string, options?: RequestInit): Promise<T> {
  const response = await apiFetch(path, {
    ...options,
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      ...options?.headers,
    },
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `Request failed: ${response.status}`);
  }

  if (response.status === 204) return undefined as T;
  return response.json();
}

export async function initVideoUpload(
  request: InitUploadRequest,
  accessToken: string
): Promise<InitUploadResponse> {
  return apiCall(`${BASE_PATH}/init-upload`, accessToken, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
}

export async function confirmVideoUpload(
  videoUploadId: string,
  accessToken: string
): Promise<ConfirmUploadResponse> {
  return apiCall(`${BASE_PATH}/${videoUploadId}/confirm`, accessToken, {
    method: 'POST',
  });
}

export async function submitExternalLink(
  request: ExternalLinkRequest,
  accessToken: string
): Promise<VideoStatusResponse> {
  return apiCall(`${BASE_PATH}/external-link`, accessToken, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
}

export async function getVideoStatus(
  accessToken: string
): Promise<VideoStatusResponse> {
  return apiCall(`${BASE_PATH}/status`, accessToken);
}

export async function deleteVideo(
  accessToken: string
): Promise<void> {
  return apiCall(`${BASE_PATH}`, accessToken, {
    method: 'DELETE',
  });
}

export function uploadToPresignedUrl(
  presignedUrl: string,
  file: File | Blob,
  onProgress?: (percent: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed: ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Upload failed')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
}
